import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import './CardProjectSummary.css';

/**
 * Project summary card for the Projects view (all-projects level). Figma
 * Projects `8007:4572` / `8001:3772`: title with task count, description,
 * up to three recent-task rows (32px type tile + name), and a Tiny Ghost
 * "+N tasks" control when the project has more than three tasks.
 */
export type CardProjectSummaryProps = {
  title?: string;
  description?: string;
  /** Recent-task rows, typically `CardProjectSummaryTask` items. */
  children?: ReactNode;
  editLabel?: string;
  onEditClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Right end of the title row, typically a dots trigger wrapped in a menu. */
  moreActions?: ReactNode;
  /**
   * Overflow control under the task list (Figma Tiny Ghost "+N tasks").
   * Hidden when omitted.
   */
  moreTasks?: ReactNode;
  className?: string;
  /**
   * Selected state for bulk actions: light-blue fill and a 1px brand border
   * around the whole card. Transparent default border keeps layout size stable.
   */
  selected?: boolean;
  /** Card-level click handler; also adds the clickable cursor class. */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
};

export type CardProjectSummaryTaskProps = {
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  /**
   * When set, only the icon and task name open that task. Empty space to the
   * right of the name is not a hit target, so the parent card can open the project.
   */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
};

/** One task row: 32px type tile + name. Figma Projects `8007:4854`. */
export function CardProjectSummaryTask({
  icon,
  children,
  className = '',
  onClick,
}: CardProjectSummaryTaskProps) {
  const classNames = ['spark-card-project-summary-task', className].filter(Boolean).join(' ');
  const content = (
    <>
      {icon}
      <span className="spark-card-project-summary-task-name">{children}</span>
    </>
  );
  if (onClick) {
    return (
      <div className={classNames}>
        <button
          type="button"
          className="spark-card-project-summary-task-hit"
          onClick={(event) => {
            event.stopPropagation();
            onClick(event);
          }}
        >
          {content}
        </button>
      </div>
    );
  }
  return <div className={classNames}>{content}</div>;
}

export function CardProjectSummary({
  title = 'Untitled-Project',
  description = '',
  children,
  editLabel = 'Edit project',
  onEditClick,
  moreActions,
  moreTasks,
  className = '',
  selected = false,
  onClick,
}: CardProjectSummaryProps) {
  const hasTaskBody = Boolean(children) || Boolean(moreTasks);

  return (
    <article
      className={[
        'spark-card-project-summary',
        onClick && 'spark-card-project-summary-clickable',
        selected && 'spark-card-project-summary-selected',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-selected={selected || undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event: KeyboardEvent<HTMLElement>) => {
              // Only activate for the card itself, not focused children.
              if (event.target !== event.currentTarget) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(event as unknown as MouseEvent<HTMLElement>);
              }
            }
          : undefined
      }
    >
      <div className="spark-card-project-summary-heading">
        <div className="spark-card-project-summary-title-row">
          <h3 className="spark-card-project-summary-title">{title}</h3>
          {onEditClick ? (
            <Button
              variant="ghost"
              size="tiny"
              type="button"
              aria-label={editLabel}
              className="spark-card-project-summary-edit"
              icon={<SparkIcon slug="edit" size="small" />}
              onClick={onEditClick}
            />
          ) : null}
          {moreActions ? (
            <div className="spark-card-project-summary-actions">{moreActions}</div>
          ) : null}
        </div>
        {description ? (
          <p className="spark-card-project-summary-description">{description}</p>
        ) : null}
      </div>
      {hasTaskBody ? (
        <div className="spark-card-project-summary-body">
          {children ? (
            <div className="spark-card-project-summary-task-list">{children}</div>
          ) : null}
          {moreTasks ? (
            <div className="spark-card-project-summary-more-tasks">{moreTasks}</div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
